import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL, authFetch, setLicencaSuspensaHandler, setSessionExpiredHandler } from "./api";
import LoginPage from "./LoginPage";
import AdminLoginPage from "./AdminLoginPage";
import MailingFormPage from "./MailingFormPage";
import ProfilePage from "./ProfilePage";
import AppLayout from "./layout/AppLayout";
import DashboardPage from "./DashboardPage";
import AdminPanelPage from "./AdminPanelPage";
import LicencaSuspensa from "./LicencaSuspensa";
import "./index.css";
import "./MailingFormPage.css";

const PAGE_TO_PATH = {
  dashboard: "/dashboard",
  mailing:   "/mailing",
  profile:   "/profile",
  admin:     "/admin",
};

const RECHECAGEM_SUSPENSA_MS = 60000;

const PATH_TO_PAGE = Object.fromEntries(
  Object.entries(PAGE_TO_PATH).map(([k, v]) => [v, k])
);



function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("mailing_user");
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch {
        return null;
      }
    }
    return null;
  });


  async function recarregarUsuario() {
    try {
      const res = await authFetch(`${API_BASE_URL}/auth/me/`);

      if (!res.ok) {
        setUser(null);
        localStorage.removeItem("mailing_user");
        return;
      }

      const freshUser = await res.json();
      setUser(freshUser);
      localStorage.setItem("mailing_user", JSON.stringify(freshUser));
    } catch (e) {
      // ignora
    }
  }

  useEffect(() => {
    recarregarUsuario();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suspensão no meio da sessão: qualquer 403 `licenca_suspensa` troca a tela na hora.
  useEffect(() => {
    setLicencaSuspensaHandler(() =>
      setUser((atual) => (atual ? { ...atual, licenca_suspensa: true } : atual))
    );
    return () => setLicencaSuspensaHandler(null);
  }, []);

  // Pagamento confirmado: a tela volta sozinha ao re-checar o /auth/me/ (foco e intervalo curto).
  const suspensa = user?.licenca_suspensa === true;
  useEffect(() => {
    if (!suspensa) return undefined;
    const intervalo = setInterval(recarregarUsuario, RECHECAGEM_SUSPENSA_MS);
    window.addEventListener("focus", recarregarUsuario);
    return () => {
      clearInterval(intervalo);
      window.removeEventListener("focus", recarregarUsuario);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suspensa]);


  const [currentPage, setCurrentPage] = useState(
    () => PATH_TO_PAGE[location.pathname] || "dashboard"
  );

  // Sincroniza currentPage quando o usuário usa botão voltar/avançar do browser.
  // Rota que não existe mais (ex.: /cnpj, removida) cai no dashboard em vez de ficar
  // com a URL antiga na barra enquanto o conteúdo já é outro.
  useEffect(() => {
    if (!user) return;
    const page = PATH_TO_PAGE[location.pathname];
    if (page) setCurrentPage(page);
    else navigate("/dashboard", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user]);

  function handleNavigate(page) {
    setCurrentPage(page);
    navigate(PAGE_TO_PATH[page] || "/dashboard");
  }

  function handleLogin(userData) {
    setUser(userData);
    localStorage.setItem("mailing_user", JSON.stringify(userData));
    navigate("/dashboard", { replace: true });
  }

  function handleLogout() {
    fetch(`${API_BASE_URL}/auth/logout/`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
    let userKey;
    try {
      const saved = JSON.parse(localStorage.getItem("mailing_user") || "null");
      userKey = saved?.id ?? saved?.email ?? saved?.username;
    } catch {
      userKey = null;
    }
    if (userKey) localStorage.removeItem(`mailing_task_state_${userKey}`);
    setUser(null);
    localStorage.removeItem("mailing_user");
  }

  // Handler global de sessão expirada: authFetch aciona isto quando o refresh do token falha.
  useEffect(() => {
    setSessionExpiredHandler(handleLogout);
    return () => setSessionExpiredHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleUserUpdate(partial) {
    const updated = { ...user, ...partial };
    setUser(updated);
    localStorage.setItem("mailing_user", JSON.stringify(updated));
  }

  // Cliente (login pelo SSO da Área do Cliente) nunca é interno, mesmo com grupos herdados.
  const userGroups = user?.groups || [];
  const isInternalUser =
    user?.sso === true
      ? false
      : Boolean(user?.is_superuser || userGroups.includes("ADM") || userGroups.includes("SUPORTE"));

  // Cliente não tem tela de Perfil: manda para o dashboard se cair em /profile.
  useEffect(() => {
    if (user && currentPage === "profile" && !isInternalUser) {
      handleNavigate("dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentPage, isInternalUser]);

  // Se não estiver logado: rotas de auth
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/admin-login" element={<AdminLoginPage onLogin={handleLogin} />} />
        <Route path="/ativar-conta" element={<Navigate to="/login" replace />} />
        <Route path="/esqueci-senha" element={<Navigate to="/login" replace />} />
        <Route path="/resetar-senha" element={<Navigate to="/login" replace />} />
        <Route path="/compra-aprovada" element={<Navigate to="/login" replace />} />
        <Route path="/pagamento-pendente" element={<Navigate to="/login" replace />} />
        <Route index element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (user.licenca_suspensa === true) {
    return (
      <AppLayout user={user} onLogout={handleLogout} currentPage={currentPage} onNavigate={handleNavigate} bloqueado>
        <LicencaSuspensa />
      </AppLayout>
    );
  }

  // Se estiver logado: app normal
  return (
    <AppLayout
      user={user}
      onLogout={handleLogout}
      currentPage={currentPage}
      onNavigate={handleNavigate}
    >
      {currentPage === "dashboard" && <DashboardPage onNavigate={handleNavigate} />}
      {currentPage === "mailing" && <MailingFormPage user={user} />}
      {currentPage === "profile" && isInternalUser && (<ProfilePage user={user} onUserUpdate={handleUserUpdate} />)}
      {currentPage === "admin" && <AdminPanelPage user={user} />}
    </AppLayout>
  );
}

export default App;
